import React from 'react';
import { useHistory } from 'react-router-dom';

import { useEntitlements } from '../contexts/Entitlements';
import './require-feature.scss';

/**
 * Locked-feature screen — what a user sees when they reach a page their
 * store's plan doesn't include (directly by URL, or via a nav item shown
 * with a lock).
 *
 * Two variants, per the gating design doc:
 *  - locked  -> named feature + upgrade invitation (upgradeable features)
 *  - hidden  -> generic "not available" with NO feature name or upsell
 *               (Future Release / On Request features are never advertised)
 */
export function LockedFeature({ featureLabel, variant = 'locked' }) {
  const history = useHistory();

  return (
    <div className="rc-locked-feature">
      <div className="rc-locked-feature__card">
        <div className="rc-locked-feature__icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="5" y="10.5" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 10V7.5C8 5.3 9.8 3.5 12 3.5C14.2 3.5 16 5.3 16 7.5V10" stroke="currentColor" strokeWidth="1.6" />
          </svg>
        </div>
        {variant === 'locked' ? (
          <>
            <h3>{featureLabel ? `${featureLabel} is not in your plan` : 'This feature is not in your plan'}</h3>
            <p>
              Your store&apos;s current plan doesn&apos;t include this feature. Your store owner can
              upgrade the plan, or contact Retail Circle to add it.
            </p>
          </>
        ) : (
          <>
            <h3>This page isn&apos;t available</h3>
            <p>There&apos;s nothing to see here right now.</p>
          </>
        )}
        <button type="button" className="rc-btn rc-btn--small" onClick={() => history.push('/')}>
          Back to dashboard
        </button>
      </div>
    </div>
  );
}

/**
 * Route/section guard:
 *
 *   <RequireFeature feature="lookbook_generation">
 *     <LookbooksPage />
 *   </RequireFeature>
 *
 * Renders children while gating is off/shadow, while entitlements are still
 * loading, and when the feature is available — the fail-open contract lives
 * in EntitlementsContext, this component just applies it. When enforce mode
 * withholds the feature it renders the locked screen ('hidden' variant for
 * Future Release keys, per featureState()).
 */
export function RequireFeature({ feature, children }) {
  const { loading, gatingActive, hasFeature, featureState, catalog } = useEntitlements();

  if (loading || !gatingActive || hasFeature(feature)) {
    return children;
  }

  const meta = catalog.find((f) => f.key === String(feature).split('|')[0]);

  return <LockedFeature featureLabel={meta?.label} variant={featureState(feature)} />;
}

export default RequireFeature;
